import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import GiveSuccess from "./pages/GiveSuccess";
import GiveCancel from "./pages/GiveCancel";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import Donations from "./pages/admin/Donations";
import Donors from "./pages/admin/Donors";
import MediaLibrary from "./pages/admin/MediaLibrary";
import LatestServices from "./pages/admin/LatestServices";
import PrayerRequests from "./pages/admin/PrayerRequests";
import Members from "./pages/admin/Members";
import Announcements from "./pages/admin/Announcements";
import PushBroadcast from "./pages/admin/PushBroadcast";
import OnboardingDrips from "./pages/admin/OnboardingDrips";
import Settings from "./pages/admin/Settings";
import Logs from "./pages/admin/Logs";
import AdminProtected from "./components/admin/AdminProtected";
import { AuthProvider } from "./lib/auth";
import { MemberProvider } from "./lib/member";
import MemberProtected from "./components/MemberProtected";
import PortalLogin from "./pages/PortalLogin";
import PortalReset from "./pages/PortalReset";
import MemberAuthCallback from "./pages/MemberAuthCallback";
import Portal from "./pages/Portal";
import SeoLanding from "./pages/SeoLanding";
import Article, { ArticlesIndex } from "./pages/Article";
import NotFound from "./pages/NotFound";
import {
  RequestPrayerPage, JoinUpdatesPage, ServiceRecordingsPage, EventsPage,
  GivePage, ContactPage, AboutPage, LeadershipPage, FireHubsPage, MentorshipPage,
} from "./pages/SitePages";
import { SEO_LANDING_PAGES } from "./data/seoLandingPages";

// SEO landing routes that are NOT served by a dedicated functional page (above).
// Functional pages: /prophetic-mentorship and /fire-hubs (overridden by SitePages).
const FUNCTIONAL_OVERRIDES = new Set(["/prophetic-mentorship", "/fire-hubs"]);

function HashAwareRouter() {
  const location = useLocation();
  // Synchronous OAuth callback detection — runs before any other route logic
  if (location.hash?.includes("session_id=")) {
    if (location.pathname.startsWith("/portal")) return <MemberAuthCallback />;
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />

      {/* Public site pages (functional) */}
      <Route path="/about" element={<AboutPage />} />
      <Route path="/leadership" element={<LeadershipPage />} />
      <Route path="/request-prayer" element={<RequestPrayerPage />} />
      <Route path="/join-updates" element={<JoinUpdatesPage />} />
      <Route path="/service-recordings" element={<ServiceRecordingsPage />} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/give" element={<GivePage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/fire-hubs" element={<FireHubsPage />} />
      <Route path="/prophetic-mentorship" element={<MentorshipPage />} />

      {/* Articles */}
      <Route path="/articles" element={<ArticlesIndex />} />
      <Route path="/articles/:slug" element={<Article />} />

      {/* Data-driven SEO landing pages */}
      {SEO_LANDING_PAGES.filter((p) => !FUNCTIONAL_OVERRIDES.has(p.path)).map((p) => (
        <Route key={p.path} path={p.path} element={<SeoLanding path={p.path} />} />
      ))}

      {/* Existing flows */}
      <Route path="/give/success" element={<GiveSuccess />} />
      <Route path="/give/cancel" element={<GiveCancel />} />
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal/reset" element={<PortalReset />} />
      <Route path="/portal/auth/callback" element={<MemberAuthCallback />} />
      <Route path="/portal" element={<MemberProtected><Portal /></MemberProtected>} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/auth/callback" element={<AuthCallback />} />
      <Route
        path="/admin"
        element={
          <AdminProtected>
            <AdminLayout />
          </AdminProtected>
        }
      >
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="donations" element={<Donations />} />
        <Route path="donors" element={<Donors />} />
        <Route path="media-library" element={<MediaLibrary />} />
        <Route path="latest-services" element={<LatestServices />} />
        <Route path="prayer-requests" element={<PrayerRequests />} />
        <Route path="members" element={<Members />} />
        <Route path="announcements" element={<Announcements />} />
        <Route path="push" element={<PushBroadcast />} />
        <Route path="onboarding-drips" element={<OnboardingDrips />} />
        <Route path="settings" element={<Settings />} />
        <Route path="logs" element={<Logs />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App bg-[#050505] text-[#F6F0E6] min-h-screen antialiased">
      <BrowserRouter>
        <AuthProvider>
          <MemberProvider>
            <HashAwareRouter />
          </MemberProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster
        position="top-center"
        theme="dark"
        toastOptions={{
          style: {
            background: "#111111",
            border: "1px solid rgba(201,164,76,0.35)",
            color: "#F6F0E6",
          },
        }}
      />
    </div>
  );
}

export default App;
